import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Plus,
  Trash2,
  Edit,
  Eye,
  EyeOff,
  Loader2,
  FileText,
  Calendar,
  Star,
  StarOff,
  ExternalLink
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API,
  timeout: 30000,
});

// Add Authorization header from localStorage token
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('hireun_session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const CATEGORIES = [
  "Interview Tips",
  "Career Strategy",
  "Resume Advice",
  "Job Search",
  "Salary Negotiation",
  "Industry Insights",
  "Professional Development"
];

const BlogManagement = () => {
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    category: "Interview Tips",
    author: "Hireun Team",
    featured: false,
    published: false,
    meta_description: "",
    meta_keywords: ""
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get("/blog/admin/posts");
      setPosts(response.data.posts || []);
    } catch (error) {
      toast.error("Failed to load blog posts");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      slug: "",
      excerpt: "",
      content: "",
      category: "Interview Tips",
      author: "Hireun Team",
      featured: false,
      published: false,
      meta_description: "",
      meta_keywords: ""
    });
    setEditingPost(null);
  };

  const openNewPost = () => {
    resetForm();
    setShowEditor(true);
  };

  const openEditPost = async (postId) => {
    try {
      const response = await apiClient.get(`/blog/admin/posts/${postId}`);
      const post = response.data;
      setFormData({
        title: post.title || "",
        slug: post.slug || "",
        excerpt: post.excerpt || "",
        content: post.content || "",
        category: post.category || "Interview Tips",
        author: post.author || "Hireun Team",
        featured: post.featured || false,
        published: post.published || false,
        meta_description: post.meta_description || "",
        meta_keywords: post.meta_keywords || ""
      });
      setEditingPost(post);
      setShowEditor(true);
    } catch (error) {
      toast.error("Failed to load post");
    }
  };

  const savePost = async () => {
    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!formData.excerpt.trim()) {
      toast.error("Excerpt is required");
      return;
    }
    if (!formData.content.trim()) {
      toast.error("Content is required");
      return;
    }

    setIsSaving(true);
    try {
      if (editingPost) {
        // Update existing post
        await apiClient.put(`/blog/admin/posts/${editingPost.post_id}`, formData);
        toast.success("Post updated successfully");
      } else {
        // Create new post
        await apiClient.post("/blog/admin/posts", formData);
        toast.success("Post created successfully");
      }
      setShowEditor(false);
      resetForm();
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save post");
    } finally {
      setIsSaving(false);
    }
  };

  const togglePublish = async (postId, currentStatus) => {
    try {
      const endpoint = currentStatus 
        ? `/blog/admin/posts/${postId}/unpublish`
        : `/blog/admin/posts/${postId}/publish`;
      await apiClient.post(endpoint);
      toast.success(currentStatus ? "Post unpublished" : "Post published");
      fetchPosts();
    } catch (error) {
      toast.error("Failed to update post status");
    }
  };

  const toggleFeatured = async (postId, currentStatus) => {
    try {
      await apiClient.post(`/blog/admin/posts/${postId}/feature?featured=${!currentStatus}`);
      toast.success(currentStatus ? "Removed from featured" : "Added to featured");
      fetchPosts();
    } catch (error) {
      toast.error("Failed to update featured status");
    }
  };

  const deletePost = async (postId, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) {
      return;
    }
    try {
      await apiClient.delete(`/blog/admin/posts/${postId}`);
      toast.success("Post deleted");
      fetchPosts();
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Blog Posts</CardTitle>
            <CardDescription>Create and manage blog articles</CardDescription>
          </div>
          <Button onClick={openNewPost} data-testid="new-blog-post-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Post
          </Button>
        </CardHeader>
        <CardContent>
          {posts.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No blog posts yet</p>
              <Button variant="link" onClick={openNewPost}>
                Create your first post
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2">Title</th>
                    <th className="text-left py-3 px-2">Category</th>
                    <th className="text-left py-3 px-2">Status</th>
                    <th className="text-left py-3 px-2">Views</th>
                    <th className="text-left py-3 px-2">Created</th>
                    <th className="text-right py-3 px-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map((post) => (
                    <tr key={post.post_id} className="border-b hover:bg-secondary/20" data-testid={`blog-row-${post.post_id}`}>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          {post.featured && (
                            <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                          )}
                          <div>
                            <p className="font-medium truncate max-w-[250px]">{post.title}</p>
                            <p className="text-xs text-muted-foreground truncate max-w-[250px]">
                              /{post.slug}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <Badge variant="outline">{post.category}</Badge>
                      </td>
                      <td className="py-3 px-2">
                        {post.published ? (
                          <Badge className="bg-green-100 text-green-800">Published</Badge>
                        ) : (
                          <Badge variant="secondary">Draft</Badge>
                        )}
                      </td>
                      <td className="py-3 px-2 text-muted-foreground">
                        {post.views || 0}
                      </td>
                      <td className="py-3 px-2 text-muted-foreground">
                        {formatDate(post.created_at)}
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditPost(post.post_id)}
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => togglePublish(post.post_id, post.published)}
                            title={post.published ? "Unpublish" : "Publish"}
                          >
                            {post.published ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleFeatured(post.post_id, post.featured)}
                            title={post.featured ? "Remove from featured" : "Feature"}
                          >
                            {post.featured ? (
                              <StarOff className="w-4 h-4" />
                            ) : (
                              <Star className="w-4 h-4" />
                            )}
                          </Button>
                          {post.published && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(`/blog/${post.slug}`, '_blank')}
                              title="View"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deletePost(post.post_id, post.title)}
                            className="text-destructive hover:text-destructive"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Blog Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={(open) => {
        if (!open) {
          setShowEditor(false);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPost ? "Edit Post" : "Create New Post"}
            </DialogTitle>
            <DialogDescription>
              {editingPost ? "Update your blog post" : "Write a new blog article"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Title */}
            <div>
              <label className="text-sm font-medium mb-1 block">Title *</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter post title"
                data-testid="blog-title-input"
              />
            </div>

            {/* Slug */}
            <div>
              <label className="text-sm font-medium mb-1 block">
                Slug <span className="text-muted-foreground">(auto-generated if empty)</span>
              </label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                placeholder="url-friendly-slug"
              />
            </div>

            {/* Category & Author */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Category</label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Author</label>
                <Input
                  value={formData.author}
                  onChange={(e) => setFormData(prev => ({ ...prev, author: e.target.value }))}
                  placeholder="Author name"
                />
              </div>
            </div>

            {/* Excerpt */}
            <div>
              <label className="text-sm font-medium mb-1 block">Excerpt * <span className="text-muted-foreground">(shown in post listings)</span></label>
              <Textarea
                value={formData.excerpt}
                onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
                placeholder="Brief summary of the post (1-2 sentences)"
                rows={2}
                data-testid="blog-excerpt-input"
              />
            </div>

            {/* Content */}
            <div>
              <label className="text-sm font-medium mb-1 block">Content * <span className="text-muted-foreground">(HTML supported)</span></label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                placeholder="<h2>Introduction</h2><p>Your article content here...</p>"
                rows={12}
                className="font-mono text-sm"
                data-testid="blog-content-input"
              />
            </div>

            {/* SEO Fields */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium mb-3">SEO Settings</h4>
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Meta Description</label>
                  <Textarea
                    value={formData.meta_description}
                    onChange={(e) => setFormData(prev => ({ ...prev, meta_description: e.target.value }))}
                    placeholder="SEO description (defaults to excerpt if empty)"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Meta Keywords</label>
                  <Input
                    value={formData.meta_keywords}
                    onChange={(e) => setFormData(prev => ({ ...prev, meta_keywords: e.target.value }))}
                    placeholder="keyword1, keyword2, keyword3"
                  />
                </div>
              </div>
            </div>

            {/* Options */}
            <div className="flex items-center gap-6 border-t pt-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.published}
                  onChange={(e) => setFormData(prev => ({ ...prev, published: e.target.checked }))}
                  className="w-4 h-4"
                />
                <span className="text-sm">Publish immediately</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
                  className="w-4 h-4"
                />
                <span className="text-sm">Featured post</span>
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditor(false)}>
              Cancel
            </Button>
            <Button onClick={savePost} disabled={isSaving} data-testid="save-blog-post-btn">
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                editingPost ? "Update Post" : "Create Post"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BlogManagement;
